import { isEmpty, isNumber, sum } from 'lodash';
import moment from 'moment';
import { getConsumptionValue } from 'shared/helpers/temperature';
import { getLocationDataByDeviceId } from 'shared/helpers/device';

export const DATA_CONSUMPTION_BYTES_PER_GB = 1073741824;

export const formatDataConsumptionTotal = (value) =>
  Math.round(((Number(value) || 0) / DATA_CONSUMPTION_BYTES_PER_GB) * 100) / 100;

const DATA_CONSUMPTION_DIVISORS = {
  mb: 1024 * 1024,
  gb: 1024 * 1024 * 1024,
};

// Picks the unit for a single reading.
export const dataConsumptionUnit = (value) =>
  isNumber(value) && Math.abs(value) >= DATA_CONSUMPTION_DIVISORS.gb
    ? 'gb'
    : 'mb';

// Picks one unit for a whole series. A chart axis and a list share a single
// label, so the unit has to come from the largest reading rather than each
// point choosing its own — otherwise a 900mb bar and a 2gb bar would be
// plotted against the same axis at heights 900 and 2.
export const dataConsumptionSeriesUnit = (values) =>
  dataConsumptionUnit(
    (values ?? [])
      .filter(Number.isFinite)
      .reduce((max, value) => Math.max(max, Math.abs(value)), 0),
  );

// Numeric conversion, for chart series and axis ticks that need a number.
// Pass `unit` to hold a whole series to one scale.
export const toDataConsumption = (value, unit = dataConsumptionUnit(value)) =>
  isNumber(value)
    ? value / (DATA_CONSUMPTION_DIVISORS[unit] ?? DATA_CONSUMPTION_DIVISORS.mb)
    : null;

// Returns the display value, or null when there is nothing to show, so each
// caller keeps its own placeholder ('--' / '-').
export const formatDataConsumption = (
  value,
  unit = dataConsumptionUnit(value),
) => {
  const converted = toDataConsumption(value, unit);
  return converted === null ? null : converted.toFixed(2);
};

const CHART_BAR_LABEL_FORMATS = {
  months: 'MMM',
  weeks: 'MMM',
  days: 'DD',
  hours: 'HH:mm',
  minutes: 'HH:mm',
};

export const formatChartBarLabel = (id, intervalUnit) => {
  // moment(undefined) is today, so a missing id would silently label the bar
  // with the current month rather than showing nothing.
  if (id === null || id === undefined) return '';

  const period = moment(id);
  return period.isValid()
    ? period.format(CHART_BAR_LABEL_FORMATS[intervalUnit] ?? 'MMM')
    : '';
};

const CHART_TIME_AXIS_UNITS = {
  months: 'MONTH',
  weeks: 'WEEK',
  days: 'DAY',
  hours: 'HOUR',
  minutes: 'MINUTE',
};

export const chartTimeAxisTitle = (intervalUnit) =>
  `TIME (${CHART_TIME_AXIS_UNITS[intervalUnit] ?? 'DAY'})`;

// The telemetry search needs all four parts. The dates are passed straight to
// formatTime, so an unset one has to be caught here rather than downstream.
export const isTelemetrySearchReady = (
  isSystemSelected,
  isSwitchSelected,
  scheduleData,
) =>
  Boolean(isSystemSelected) &&
  Boolean(isSwitchSelected) &&
  Boolean(scheduleData?.start?.date) &&
  Boolean(scheduleData?.end?.date);

export const getGraphTypeKey = (graphType) => {
  let result;
  switch (graphType) {
    case 'heater_graph':
      result = 'heaterGraphData';
      break;
    case 'enclosure_graph':
    case 'gas_enclosure_graph':
      result = 'enclosureGraphData';
      break;
    case 'outside_graph':
      result = 'outsideGraphData';
      break;
    case 'gas_graph':
      result = 'gasGraphData';
      break;
    case 'snow_graph':
    case 'gas_snow_graph':
      result = 'snowGraphData';
      break;
    case 'wind_graph':
    case 'gas_wind_graph':
      result = 'windGraphData';
      break;
    case 'energy':
    case 'fuel':
      result = 'energyGasConsumptionGraphData';
      break;
  }
  return result;
};

export const getTotalSearchValues = (data, type, swtName, isF, isDc) => {
  const raw = sum(
    data.map((el) => {
      const machine = isDc ? el : el.data;
      return sum(
        machine
          .filter((el) => typeof el.startTime === 'number' || el.start_date)
          .map((el) => {
            switch (type) {
              case 'hourOfUsage':
                return Math.floor((el.hourOfUsage || 0) / 3600);
              case 'consumption':
                return getConsumptionValue(swtName, el.consumption, isF);
              case 'total':
                return el.total || 0;
            }
          })
      );
    })
  );
  return type === 'total'
    ? formatDataConsumptionTotal(raw)
    : Math.floor(raw);
};

export const getTotalMachineValues = (data, type, swtName, isF) => {
  const raw = sum(
    data
      .filter((el) => typeof el.startTime === 'number' || el.start_date)
      .map((el) => {
        switch (type) {
          case 'hourOfUsage':
            return Math.floor((el.hourOfUsage || 0) / 3600);
          case 'consumption':
            return getConsumptionValue(swtName, el.consumption, isF);
          case 'total':
            return el.total || 0;
        }
      })
  );
  return type === 'total'
    ? formatDataConsumptionTotal(raw)
    : Math.floor(raw);
};

const getColorBlockChart = (index, swtName) => {
  const isOdd = index % 2 === 1;
  switch (swtName) {
    case 'ess':
      return isOdd ? '#83FFFF' : '#70C7F0';
    case 'tgs':
      return isOdd ? '#FF7800' : '#FFC400';
    case 'tes':
      return isOdd ? '#95FF45' : '#D5FF45';
  }
};

export const formatTelemetryChartData = (data, swtName, swtSwitch, isF) => {
  const color =
    swtName === 'ess' ? '#83FFFF' : swtName === 'tgs' ? '#FF7800' : '#95FF45';

  const intervalData = data.map((machine) => {
    const machineData = getLocationDataByDeviceId(machine.deviceId, swtSwitch)[
      machine.deviceId
    ];
    if(isEmpty(machineData)){
      return {
        machine: machine.deviceId,
        switch: `unassigned`,
        totalConsumption: 0,
        totalUsageHours: 0,
        barColor: color,
      };
    }

    return {
      machine: machine.deviceId,
      switch: `${machineData.locationName} - ${machineData.machineName}`,
      totalConsumption: getTotalMachineValues(
        machine.data,
        'consumption',
        swtName,
        isF
      ),
      totalUsageHours: getTotalMachineValues(machine.data, 'hourOfUsage'),
      barColor: color,
      ...machine.data
        .filter((el) => typeof el.startTime === 'number')
        .map((el, index) => {
          const intervalName = el.startTime;
          return {
            [intervalName]: getConsumptionValue(swtName, el.consumption, isF),
            [`${intervalName}TEC`]: `${getConsumptionValue(
              swtName,
              el.consumption,
              isF
            )} kw`,
            [`${intervalName}THU`]: `${Math.floor(
              (el.hourOfUsage || 0) / 3600
            )} hours`,
            [`${intervalName}Color`]: getColorBlockChart(index, swtName),
          };
        })
        .reduce((cur, result) => {
          return { ...result, ...cur };
        }, {}),
    };
  });

  const intervalKeys = [
    ...new Set(
      data
        .map((machine) => {
          return machine.data
            .filter((el) => typeof el.startTime === 'number')
            .map((el) => el.startTime);
        })
        .flat()
    ),
  ].sort();

  return {
    intervalData,
    intervalKeys,
  };
};
